import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsQueryKey,
  getApiV1IndividualpaymentParcelsParcelidInvoicesQueryKey,
  usePostApiV1IndividualpaymentParcelsParcelidInvoiceFuturePayment,
  usePostApiV1IndividualpaymentParcelsParcelidInvoiceFuturePaymentVanzolini,
} from '@/shared/api/generated';

import type { PlanWriteError } from '../utils/planWriteError';
import { classifyPlanWriteError } from '../utils/planWriteError';

/**
 * Teto de caracteres do corpo da NF.
 *
 * O endpoint recebe `invoiceBody` como **query param**, não no corpo da request:
 * o texto viaja na URL. Não há limite declarado no spec, mas servidor e proxy
 * têm o seu (a faixa usual é 4–8 KB para a linha de request inteira), e o erro
 * que volta ao estourar não diz que o problema foi o tamanho. O limite no campo
 * transforma isso em algo que o operador vê antes de enviar.
 */
export const INVOICE_BODY_MAX_LENGTH = 1000;

/**
 * Parceiro que emite a nota antecipada.
 *
 * São dois endpoints irmãos, com os mesmos dois query params e a mesma semântica
 * — o legado tinha um botão para cada, e a linha da grid diz qual está
 * disponível por `hasIntegrationFealq` / `hasIntegrationVanzolini`.
 */
export type FuturePaymentProvider = 'fealq' | 'vanzolini';

/** O que o operador informa no diálogo de pagamento futuro. */
export interface ParcelFuturePaymentInput {
  /** Corpo da NF. Vazio é omitido da request. */
  readonly invoiceBody: string;
  /** E-mail de envio. Vazio é omitido da request. */
  readonly invoiceEmail: string;
}

/**
 * Antecipação da emissão da nota fiscal de uma parcela em pagamento futuro.
 *
 * Cobre `POST /individualpayment/parcels/{parcelId}/invoice-future-payment`, que
 * marca a antecipação com a data de hoje, o corpo informado e o usuário da
 * sessão. O e-mail é apenas **gravado** aqui — o envio fica com a camada de
 * notificação, então o sucesso desta chamada não significa que alguém recebeu
 * algo.
 *
 * **Os dois campos são query params opcionais, não corpo JSON.** Campo em branco
 * é omitido da URL em vez de mandado como string vazia: `?invoiceBody=` gravaria
 * um corpo vazio no lugar de deixar o backend com o seu padrão.
 *
 * Duas queries são invalidadas no sucesso: os comprovantes da parcela, porque a
 * NF antecipada aparece neles, e a grid de parcelas do contrato, que é onde
 * `paymentFuture` e `futurePaymentDate` são lidos. O cabeçalho do contrato fica
 * de fora — a marcação não move valor, vencimento nem status de pagamento, então
 * nenhum agregado dele muda.
 *
 * O parceiro escolhe entre os dois endpoints irmãos — `.../invoice-future-payment`
 * (FEALQ) e `.../invoice-future-payment-vanzolini`. Os dois recebem os mesmos
 * query params e invalidam as mesmas queries, então só a mutation muda.
 *
 * @param financialPersonId - Contrato da rota; `null` só pula a invalidação da
 *   grid, sem impedir a escrita.
 * @param parcelId - Parcela alvo; `null` torna o `mark` inerte, evitando request
 *   com `NaN` na URL.
 * @param provider - Parceiro emissor. Padrão `fealq`, que é a regra 13 do legado.
 * @returns `{ mark, isPending, isError, error, isForbidden, isServerError, reset }`
 *
 * @example
 * ```tsx
 * const { mark, isPending } = useParcelFuturePayment(101, 42, 'vanzolini');
 * mark({ invoiceBody: 'Curso X — turma 2026', invoiceEmail: '' }, { onSuccess: close });
 * ```
 */
export function useParcelFuturePayment(
  financialPersonId: number | null,
  parcelId: number | null,
  provider: FuturePaymentProvider = 'fealq',
) {
  const queryClient = useQueryClient();
  // As duas mutations são instanciadas sempre — hook não pode ser condicional —
  // e o parceiro escolhe qual dispara.
  const fealqMutation = usePostApiV1IndividualpaymentParcelsParcelidInvoiceFuturePayment();
  const vanzoliniMutation =
    usePostApiV1IndividualpaymentParcelsParcelidInvoiceFuturePaymentVanzolini();
  const mutation = provider === 'vanzolini' ? vanzoliniMutation : fealqMutation;

  const mark = useCallback(
    (
      input: ParcelFuturePaymentInput,
      options?: {
        readonly onSuccess?: () => void;
        readonly onError?: (error: PlanWriteError) => void;
      },
    ) => {
      if (parcelId === null) return;

      const invoiceBody = input.invoiceBody.trim();
      const invoiceEmail = input.invoiceEmail.trim();

      // `exactOptionalPropertyTypes` está ligado: chave presente com `undefined`
      // não equivale a chave ausente, então os campos vazios são omitidos.
      const params = {
        ...(invoiceBody === '' ? {} : { invoiceBody }),
        ...(invoiceEmail === '' ? {} : { invoiceEmail }),
      };

      mutation.mutate(
        { parcelId, ...(Object.keys(params).length === 0 ? {} : { params }) },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey: getApiV1IndividualpaymentParcelsParcelidInvoicesQueryKey(parcelId),
            });

            if (financialPersonId !== null) {
              void queryClient.invalidateQueries({
                queryKey:
                  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsQueryKey(
                    financialPersonId,
                  ),
              });
            }

            options?.onSuccess?.();
          },
          onError: error => options?.onError?.(classifyPlanWriteError(error)),
        },
      );
    },
    [financialPersonId, parcelId, mutation, queryClient],
  );

  const failure = classifyPlanWriteError(mutation.error);

  return {
    mark,
    isPending: mutation.isPending,
    isError: mutation.isError,
    /** Falha classificada, com a mensagem do backend quando houver. */
    error: failure,
    isForbidden: failure.kind === 'forbidden',
    isServerError: failure.kind === 'server',
    reset: mutation.reset,
  } as const;
}
